import React from 'react';
import { useRealTimeStatus } from '../contexts/RealTimeStatusContext';
import StatusChangeAnimation from './StatusChangeAnimation';

/**
 * RealTimeAnimationManager Component
 * 
 * Manages and displays all active status change animations
 * from the real-time status context. Handles animation
 * positioning, lifecycle, and cleanup.
 * 
 * Requirements: 1.4, 2.5, 4.5 - Status change animations and visual feedback
 */
const RealTimeAnimationManager = ({ 
  maxAnimations = 5,
  animationDuration = 3000,
  stackDirection = 'down' // 'down' or 'up'
}) => {
  const { activeAnimations, removeAnimation } = useRealTimeStatus();

  // Convert animations map to array and sort by creation time
  const animationsArray = Array.from(activeAnimations.entries())
    .map(([key, animation]) => ({ key, ...animation }))
    .sort((a, b) => b.startTime - a.startTime) // Newest first
    .slice(0, maxAnimations); // Limit number of animations

  // Handle animation completion
  const handleAnimationComplete = (key) => {
    removeAnimation(key);
  };

  // Calculate position for each animation
  const getAnimationStyle = (index) => {
    const offset = index * 80; // 80px spacing between animations
    
    if (stackDirection === 'up') {
      return {
        bottom: `${20 + offset}px`,
        right: '20px'
      };
    } else {
      return {
        top: `${20 + offset}px`,
        right: '20px'
      };
    }
  };

  if (animationsArray.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {animationsArray.map((animation, index) => (
        <div
          key={animation.key}
          className="absolute pointer-events-auto"
          style={getAnimationStyle(index)}
        >
          <StatusChangeAnimation
            type={animation.type}
            data={animation.data}
            duration={animationDuration}
            onComplete={() => handleAnimationComplete(animation.key)}
          />
        </div>
      ))}
    </div>
  );
};

export default RealTimeAnimationManager;
